<template>
	<tr
		v-if="isChart && isRender"
		class="ms-tr shimmer-row"
		:class="[
			isBorderTr ? '' : 'ms-tr-noborder',
			isActionView &&
			dataRow &&
			dataRow.hasOwnProperty('IsViewed') &&
			!dataRow.IsViewed
				? 'unviewed'
				: '',
			isSelected ? 'row-checked' : '',
		]"
	>
		<td v-if="draggable" class="ms-td multiple-cell">
			<div class="shimmer"></div>
		</td>
		<td v-if="multiple" class="ms-td multiple-cell">
			<div class="shimmer"></div>
		</td>
		<td
			v-if="widgetOptionsLeft"
			:class="isGridReport ? 'shimmer-report' : 'shimmer-widget'"
		>
			<div class="shimmer"></div>
		</td>
		<td v-if="serial" class="ms-td serial text-center">
			<div class="shimmer"></div>
		</td>
		<td
			v-for="(col, colIndex) in columns"
			:key="col.dataField"
			class="ms-td ms-col-td"
			:class="col.cssCellClass"
			:style="styleWidth(col, false)"
			@click="row_click"
			@dblclick="row_dblclick"
		>
			<div class="shimmer"></div>
		</td>
		<td
			v-if="(rowActions && rowActions.length > 0) || rowActionsWidget"
			class="ms-td ms-row-actions-cell"
		>
			<div class="shimmer"></div>
		</td>
		<td
			v-if="widgetOptions"
			class="ms-td widget-item"
			:style="styleWidth(widgetOptions, true)"
		>
			<div class="shimmer"></div>
		</td>
	</tr>
	<tr
		v-else-if="dataRow.IsTitle && columnTitle"
		class="ms-tr"
		:class="[
			isBorderTr ? '' : 'ms-tr-noborder',
			isActionView &&
			dataRow &&
			dataRow.hasOwnProperty('IsViewed') &&
			!dataRow.IsViewed
				? 'unviewed'
				: '',
			isSelected ? 'row-checked' : '',
		]"
		tabindex="0"
		@keydown="row_keydown"
		@focus="row_focus"
	>
		<td
			:key="columnTitle.dataField"
			class="ms-td ms-col-td font-bold"
			:style="styleWidth(columnTitle, false)"
			@click="row_click"
			@dblclick="row_dblclick"
			:ref="`td-${columnTitle.dataField}`"
			:class="dataRow.IsTitle && columnTitle.IsTitle ? 'ms-td-title' : ''"
			:colspan="columns.length"
		>
			<component
				:is="columnTitle.template"
				:data="dataRow"
				:column="columnTitle"
				v-if="columnTitle.template"
				v-bind="columnTitle.templateProps"
				v-on="columnTitle.templateEvent"
			></component>
			<div
				class="flex"
				:class="[`justify-center`, columnTitle.cssClass]"
				v-else-if="columnTitle.isSerial"
			>
				<span>{{ index + 1 }}</span>
			</div>
			<div
				v-else-if="columnTitle.isHtmlText"
				v-html="dataRow[columnTitle.dataField]"
			></div>
			<div
				class="flex items-center"
				:class="[
					`justify-${columnTitle.align == 'right' ? 'end' : columnTitle.align}`,
					columnTitle.cssClass,
				]"
				v-else
			>
				<span
					:class="[
						`text-${columnTitle.align}`,
						`text-line-${columnTitle.maxLine}`,
						isTree ? `level-${dataRow.level}` : '',
					]"
					:ref="`text-${columnTitle.dataField}`"
					@click="
						typeof columnTitle.drilldown == 'function'
							? columnTitle.drilldown(dataRow)
							: null
					"
					v-tooltip="
						typeof columnTitle.tooltipCustom == 'function'
							? columnTitle.tooltipCustom(dataRow)
							: null
					"
					>{{
						getDeepData(dataRow, columnTitle.dataField)
							| formatData({
								formatType: columnTitle.formatType,
								enumName: columnTitle.enumName,
								dataRow: dataRow,
								dataField: columnTitle.dataField,
								dataFieldOther: columnTitle.dataFieldOther,
								...columnTitle.formatOptions,
							})
					}}</span
				>
				<div
					class="mi"
					:class="buildColIconClass(columnTitle)"
					v-if="columnTitle.icon && columnTitle.icon.show(dataRow, columnTitle)"
					v-tooltip="buildColIconTooltip(columnTitle)"
				></div>
				<div v-if="widgetColumn && columnTitle.isIcon">
					<component
						:is="widgetColumn.components"
						:dataRow="dataRow"
						@widgetEvent="widgetEvent"
						:col="columnTitle"
						:style="styleWidth(columnTitle, true)"
					></component>
				</div>
			</div>
		</td>
	</tr>
	<tr
		class="ms-tr"
		:class="[
			isBorderTr ? '' : 'ms-tr-noborder',
			isActionView &&
			dataRow &&
			dataRow.hasOwnProperty('IsViewed') &&
			!dataRow.IsViewed
				? 'unviewed'
				: '',
			isSelected ? 'row-checked' : '',
		]"
		tabindex="0"
		@keydown="row_keydown"
		@focus="row_focus"
		v-else-if="isRender"
	>
		<td v-if="draggable" class="ms-td draggable-cell">
			<div
				class="mi-new-v2 mi-16 mi-icon-v2-draggable draggable-icon flex justify-center items-center cursor-pointer"
			></div>
		</td>
		<td
			v-if="multiple"
			class="ms-td multiple-cell"
			:class="[
				{ 'fix-column-left': isScrollLeft && !widgetOptionsLeft },
				{ 'col-only-check': !widgetOptionsLeft },
			]"
		>
			<ms-checkbox
				:value="isSelected"
				@click="checkMultiple"
				class="center"
				ref="multiple"
				:disabled="multipleDisabled"
				checkOnly
			/>
		</td>
		<td v-if="serial" class="ms-td serial text-center">
			{{ pageSize * (currentPage - 1) + index + 1 }}
		</td>

		<td
			v-if="widgetOptionsLeft"
			class="ms-td widget-item widget-item-left multiple-cell-widget"
			:style="styleWidth(widgetOptionsLeft, true)"
			:class="{ 'fix-column-left': isScrollLeft }"
		>
			<component
				v-if="widgetOptionsLeft"
				:is="widgetOptionsLeft.components"
				:dataRow="dataRow"
				@widgetEvent="widgetEvent"
				:style="styleWidth(widgetOptionsLeft, true)"
			></component>
		</td>
		<td
			v-for="(col, colIndex) in columns"
			:key="col.dataField"
			class="ms-td ms-col-td"
			:class="[
				col.cssCellClass,
				{
					/* col.truncate opt-in thắng mọi loại cột: template/icon/serial/html khai truncate=true
					   thì vẫn truncate. Cột thường (text thuần) truncate mặc định khi truncateOverflow bật. */
					'ms-col-td-truncate':
						truncateOverflow &&
						(col.truncate ||
							(!col.template &&
								!col.isIcon &&
								!col.isSerial &&
								!col.isHtmlText)),
					/* col.truncate + template: root element template bị ép truncate qua CSS deep */
					'ms-col-td-truncate-template':
						truncateOverflow && col.truncate && col.template,
				},
			]"
			:style="styleWidth(col, false)"
			@click="row_click"
			@dblclick="row_dblclick"
			:ref="`td-${col.dataField}`"
			v-tooltip="
				truncateOverflow && col.truncate && col.template
					? buildTemplateTooltip(col)
					: null
			"
		>
			<component
				:is="col.template"
				:data="dataRow"
				:column="col"
				v-if="col.template"
				v-bind="col.templateProps"
				v-on="col.templateEvent"
				@dataAction="handleAction"
			></component>
			<div
				class="flex"
				:class="[`justify-center`, col.cssClass]"
				v-else-if="col.isSerial"
			>
				<span>{{ index + 1 }}</span>
			</div>
			<div v-else-if="col.isHtmlText" v-html="dataRow[col.dataField]"></div>
			<div
				class="flex items-center"
				:class="[
					`justify-${col.align == 'right' ? 'end' : col.align}`,
					col.cssClass,
				]"
				v-else
			>
				<span
					v-if="!col.icon || !col.icon.hideText"
					:class="[
						`text-${col.align}`,
						/* Khi bật truncateOverflow: ẩn text-line-N vì -webkit-box + white-space:pre-line
						   override white-space:nowrap của truncate → text wrap thay vì ellipsis */
						truncateOverflow ? '' : `text-line-${col.maxLine}`,
						isTree ? `level-${dataRow.level}` : '',
						truncateOverflow ? 'ms-col-text-truncate' : '',
					]"
					:ref="`text-${col.dataField}`"
					@click="
						typeof col.drilldown == 'function' ? col.drilldown(dataRow) : null
					"
					v-tooltip="
						typeof col.tooltipCustom == 'function'
							? col.tooltipCustom(dataRow)
							: truncateOverflow
							? buildOverflowTooltip(col)
							: null
					"
					>{{
						getDeepData(dataRow, col.dataField)
							| formatData({
								formatType: col.formatType,
								enumName: col.enumName,
								dataRow: dataRow,
								dataField: col.dataField,
								dataFieldOther: col.dataFieldOther,
								...col.formatOptions,
							})
					}}</span
				>
				<div
					class="mi"
					:class="buildColIconClass(col)"
					v-if="col.icon && col.icon.show(dataRow, col)"
					v-tooltip="buildColIconTooltip(col)"
				></div>
				<div v-if="widgetColumn && col.isIcon">
					<component
						:is="widgetColumn.components"
						:dataRow="dataRow"
						@widgetEvent="widgetEvent"
						:col="col"
						:style="styleWidth(col, true)"
					></component>
				</div>
			</div>
		</td>
		<td
			v-if="(rowActions && rowActions.length > 0) || rowActionsWidget"
			class="ms-td ms-row-actions-cell"
			@click.stop
		>
			<div class="ms-row-actions" v-if="rowActionsWidget">
				<component
					:is="rowActionsWidget"
					:dataRow="dataRow"
					:data="dataRow"
					v-bind="rowActionsWidgetProps"
					@widgetEvent="widgetEvent"
				></component>
			</div>
			<div class="ms-row-actions" v-else-if="visibleRowActions.length > 0">
				<row-action-icon-button
					v-for="(action, actionIndex) in visibleRowActions"
					:key="actionIndex"
					:iconClass="buildRowActionIconClass(action)"
					:tooltip="buildRowActionTooltip(action)"
					@click.native.stop="onRowActionClick(action)"
				></row-action-icon-button>
			</div>
		</td>
		<td
			v-if="widgetOptions"
			class="ms-td widget-item"
			:style="styleWidth(widgetOptions, true)"
		>
			<component
				:is="widgetOptions.components"
				:dataRow="dataRow"
				:ref="`widget-${index}`"
				:isMoreActionGrid="widgetOptions.isActionMore"
				@widgetEvent="widgetEvent"
			></component>
		</td>
	</tr>
</template>
<script>
import RowActionIconButton from '@/views/widget/RowActionIconButton.vue';
export default {
	name: 'msTr',
	inheritAttrs: false,
	components: {
		RowActionIconButton,
	},
	props: {
		isGridReport: {
			default: false,
			type: Boolean,
		},
		//Lưu thông tin của các cột
		columns: {},
		//Lưu dữ liệu của dòng
		dataRow: {},
		//Lưu cấu hình của cột chức năng
		widgetOptions: {},
		// Cấu hình cột bên trái
		widgetOptionsLeft: {},
		// Cột được thiết lập trong danh sách thiết lập là icon
		widgetColumn: {},
		//Ẩn/hiện cột checkbox tích chọn nhiều cột
		multiple: {
			default: false,
			type: Boolean,
		},
		//Có hiên thị kéo thả kéo thả lại dòng không
		draggable: {
			default: false,
			type: Boolean,
		},
		//Trạng thái tích chọn ở cột multiple
		isSelected: {
			default: false,
			type: Boolean,
		},
		index: {
			default: null,
			type: Number,
		},
		serial: {
			default: false,
			type: Boolean,
		},
		isChart: {
			default: false,
			type: Boolean,
		},
		currentPage: {
			default: 1,
			type: Number,
		},
		pageSize: {
			default: 20,
			type: Number,
		},
		multipleDisabled: {
			default: false,
			type: Boolean,
		},
		isScrollLeft: {
			default: false,
			type: Boolean,
		},
		isTree: {
			default: false,
			type: Boolean,
		},
		isBorderTr: {
			default: true,
			typeof: Boolean,
		},
		isBorderTh: {
			default: true,
			typeof: Boolean,
		},
		isActionView: {
			default: false,
			typeof: Boolean,
		},
		/**
		 * Danh sách action hiển thị nổi lên (overlay) khi hover vào dòng
		 * Mỗi action: { key, icon: 'mi-icon-v2-xxx', tooltip: string|Function, show: Function(dataRow), onClick: Function(dataRow) }
		 * Truyền từ gridOptions.rowActions của msGridViewer xuống. Render ở 1 <td> riêng (width: 0) đặt sau cột dữ liệu cuối cùng,
		 * độc lập với số lượng/thứ tự cột động của từng danh sách — không cần cấu hình lặp lại ở từng column
		 */
		rowActions: {
			default: null,
			type: Array,
		},
		/**
		 * Component widget tùy chỉnh render vào đúng slot overlay (thay cho rowActions).
		 * Dùng khi danh sách muốn tự xử lý action/nghiệp vụ riêng bên trong 1 component (vd: UserListWidget)
		 * thay vì khai báo mảng rowActions. Nhận props dataRow/data, có thể @widgetEvent như widgetOptions.
		 * Vị trí/hover/border vẫn do base xử lý — widget chỉ cần lo icon + emit action, không cần style lại.
		 * Nếu vừa truyền rowActionsWidget vừa truyền rowActions thì rowActionsWidget được ưu tiên render.
		 */
		rowActionsWidget: {
			default: null,
		},
		/**
		 * Props tùy biến truyền thêm xuống rowActionsWidget (v-bind), bên cạnh dataRow/data mặc định.
		 * Dùng khi widget cần nhận thêm option động từ danh sách cha (vd: isMoreActionGrid của InvoiceListWidget)
		 * mà không có bảng field cố định. Mặc định {} — không truyền gì thì widget không nhận thêm attribute nào.
		 */
		rowActionsWidgetProps: {
			default: () => ({}),
			type: Object,
		},
		/**
		 * Bật truncate text trong cell: text dài quá width cột thì ẩn bớt (ellipsis),
		 * hover vào cell xem đầy đủ qua tooltip. Mặc định false để giữ behavior cũ (giãn cột).
		 */
		truncateOverflow: {
			default: true,
			type: Boolean,
		},
	},
	mounted() {
		const me = this;
		/* Row tĩnh không trigger updated() sau render → truncatedCells rỗng → tooltip null.
		   Check overflow ngay sau mount (+ $nextTick đợi DOM render xong) để đảm bảo mọi row
		   đều có flag ngay từ đầu. updated() lo các lần update sau (resize cột, data thay đổi). */
		if (!me.truncateOverflow) return;
		me.$nextTick(() => {
			me.checkTruncateOverflow();
		});
	},
	updated() {
		const me = this;
		if (!me.truncateOverflow) return;
		me.checkTruncateOverflow();
	},
	computed: {
		columnTitle() {
			const me = this;
			if (me.dataRow && me.dataRow.IsTitle) {
				return me.columns.find((x) => x.IsTitle);
			}
			return null;
		},
		/**
		 * Lọc ra action còn hiển thị cho dòng hiện tại (action.show trả về false thì ẩn)
		 */
		visibleRowActions() {
			const me = this;
			if (!me.rowActions || me.rowActions.length == 0) return [];
			return me.rowActions.filter((action) => {
				if (typeof action.show == 'function') {
					return action.show(me.dataRow);
				}
				return true;
			});
		},
	},
	data() {
		return {
			isRender: true,
			/* Map đánh dấu cell nào thực sự bị truncate (scrollWidth > clientWidth).
			   v-tooltip chỉ hiện khi text bị cắt — text ngắn → ẩn tooltip tránh noise. */
			truncatedCells: {},
		};
	},
	methods: {
		//Cập nhật lại độ rộng của cột theo độ rộng cấu hình
		//Created by LTDAT
		styleWidth(col, widget) {
			const me = this;
			let width = col._width;
			if (col.freezeWidth) {
				width = col.width;
				return { width: `${width}px`, 'min-width': `${width}px` };
			}
			if (widget) {
				width = col.width;
				if (me.columns && me.columns.length == 0) {
					return { 'min-width': `${width}px` };
				}
				return { width: `${width}px`, 'min-width': `${width}px` };
			} else {
				if (col.autoResize || (me.columns && me.columns.length <= 1)) {
					/* truncateOverflow: cột autoResize cũng phải truncate khi flag bật, không chỉ nhánh normal.
					   Trước đây nhánh autoResize early-return chỉ min-width → CSS .ms-col-text-truncate
					   (max-width:100%) không có boundary inline → truncate fail silently.
					   autoResize chỉ giữ behavior fill-space (không max-width) khi truncateOverflow=false. */
					let style = { 'min-width': `${width}px` };
					if (me.isTruncateEligible(col)) {
						style['max-width'] = `${width}px`;
						style['overflow'] = 'hidden';
					}
					return style;
				} else {
					let style = { width: `${width}px`, 'min-width': `${width}px` };
					/* truncateOverflow: áp max-width + overflow:hidden cho cột TEXT thuần và cột template
					   khai báo col.truncate=true (opt-in). Không áp cho template mặc định/icon/serial/html —
					   overflow:hidden che icon, max-width làm lệch cột hẹp.
					   th cũng nhận max-width tương ứng (xem msTh.vue) để header không lệch body. */
					if (me.isTruncateEligible(col)) {
						style['max-width'] = `${width}px`;
						style['overflow'] = 'hidden';
					}
					return style;
				}
			}
		},
		//Emit sự kiện từ widget ra ngoài
		// Created by LTDAT (16.06.2020)
		widgetEvent(data, event) {
			const me = this;
			me.$emit('widgetEvent', data, event);
		},
		//Emit sự kiện click row ra ngoài grid
		//Created by LTDAT(13/06/2020)
		row_click(e) {
			const me = this;
			me.$emit('click', me.dataRow, e);
		},
		row_focus(e) {
			const me = this;
			me.$emit('focus', me.dataRow, e);
		},
		//Emit sự kiện dblclickRow ra ngoài grid
		//Created by LTDAT(13/06/2020)
		row_dblclick(e) {
			const me = this;
			me.$emit('dblclick', me.dataRow, e);
		},
		//Emit sự kiện ấn phím ra ngoài grid
		//Created by LTDAT(13/06/2020)
		row_keydown(e) {
			const me = this;
			me.$emit('keydown', me.dataRow, e);
		},
		//Sự kiện click vào nút chọn ở cột multiple
		//Created by LTDAT (28.05.2020)
		checkMultiple(event) {
			const me = this;
			me.$emit('clickMultiple', me.dataRow);
		},

		/**
		 * Build class icon cho col.icon: type là string cố định (mặc định thêm size mi-24 để tương thích cũ)
		 * hoặc function(dataRow, col) trả về class đầy đủ (vd: cần đổi icon/size/màu theo dữ liệu dòng)
		 */
		buildColIconClass(col) {
			const me = this;
			if (!col.icon) return '';
			if (typeof col.icon.type == 'function') {
				return col.icon.type(me.dataRow, col);
			}
			return ['mi-24', col.icon.type];
		},

		/**
		 * Build tooltip cho col.icon: hỗ trợ cả object tĩnh và function nhận dataRow
		 */
		buildColIconTooltip(col) {
			const me = this;
			if (!col.icon || !col.icon.tooltip) return {};
			return typeof col.icon.tooltip == 'function'
				? col.icon.tooltip(me.dataRow, col)
				: { ...col.icon.tooltip };
		},

		/**
		 * Build nội dung tooltip đầy đủ cho cell bị truncateOverflow — hiển thị giá trị đã format,
		 * để người dùng hover xem được toàn bộ text bị ẩn bớt do giới hạn width cột.
		 */
		buildOverflowTooltip(col) {
			const me = this;
			/* Chỉ hiện tooltip khi cell thực sự bị truncate (text dài quá width) — text ngắn → null ẩn tooltip. */
			if (!me.truncatedCells[col.dataField]) return null;
			let value = me.getDeepData(me.dataRow, col.dataField);
			value = me.$options.filters.formatData(value, {
				formatType: col.formatType,
				enumName: col.enumName,
				dataRow: me.dataRow,
				dataField: col.dataField,
				dataFieldOther: col.dataFieldOther,
				...col.formatOptions,
			});
			// Trả về null để v-tooltip không hiển thị khi cell rỗng
			if (value === null || value === undefined || value === '') return null;
			return { content: String(value), html: false };
		},

		/**
		 * Build tooltip cho cột template có col.truncate=true — template tự render text nên không qua
		 * span text (nơi buildOverflowTooltip gắn v-tooltip). Hỗ trợ col.tooltipTemplate (function trả
		 * string/object) hoặc fallback theo col.dataField/col.tooltipField. Trả null khi rỗng để ẩn tooltip.
		 */
		buildTemplateTooltip(col) {
			const me = this;
			/* Chỉ hiện tooltip khi cell template thực sự bị truncate — text ngắn → null ẩn tooltip. */
			if (!me.truncatedCells[col.dataField]) return null;
			if (typeof col.tooltipTemplate == 'function') {
				return col.tooltipTemplate(me.dataRow, col);
			}
			let field = col.tooltipField || col.dataField;
			let value = me.getDeepData(me.dataRow, field);
			value = me.$options.filters.formatData(value, {
				formatType: col.formatType,
				enumName: col.enumName,
				dataRow: me.dataRow,
				dataField: field,
				dataFieldOther: col.dataFieldOther,
				...col.formatOptions,
			});
			if (value === null || value === undefined || value === '') return null;
			return { content: String(value), html: false };
		},

		/**
		 * Đánh dấu cell truncate để tooltip chỉ hiện khi text thực sự bị cắt (scrollWidth > clientWidth).
		 * Gọi từ mounted() (sau render đầu) + updated() (sau mỗi update: resize cột, data thay đổi).
		 * Chỉ check cột truncate-eligible để tránh overhead toàn bộ cột.
		 */
		checkTruncateOverflow() {
			const me = this;
			if (!me.columns || me.columns.length === 0) return;
			me.columns.forEach((col) => {
				if (!me.isTruncateEligible(col)) return;
				let el = me.getTruncateEl(col);
				if (!el) return;
				me.$set(
					me.truncatedCells,
					col.dataField,
					el.scrollWidth > el.clientWidth
				);
			});
		},

		/**
		 * Kiểm tra cột có đủ điều kiện truncate (có thể bị cắt text) — dùng trong updated() để giới hạn
		 * check overflow chỉ cho cột truncate-eligible, tránh overhead duyệt toàn bộ cột.
		 */
		isTruncateEligible(col) {
			const me = this;
			if (!me.truncateOverflow) return false;
			// col.truncate opt-in hoặc cột text thuần (không template/icon/serial/html)
			return (
				col.truncate ||
				(!col.template && !col.isIcon && !col.isSerial && !col.isHtmlText)
			);
		},

		/**
		 * Lấy element DOM dùng để check overflow của cột: span text cho cột text thường,
		 * root element của template (con đầu tiên của td) cho cột template. Trả null nếu ref chưa render.
		 */
		getTruncateEl(col) {
			const me = this;
			// Cột text thường: span có ref `text-${dataField}` — check scrollWidth của span
			let textRef = me.$refs[`text-${col.dataField}`];
			if (textRef) {
				return Array.isArray(textRef) ? textRef[0] : textRef;
			}
			// Cột template: td có ref `td-${dataField}` — lấy root element con của td (template root)
			// thay vì chính td, vì table cell scrollWidth không phản ánh nội dung tràn chính xác.
			let tdRef = me.$refs[`td-${col.dataField}`];
			if (tdRef) {
				let tdEl = Array.isArray(tdRef) ? tdRef[0] : tdRef;
				return tdEl.firstElementChild || tdEl;
			}
			return null;
		},

		/**
		 * Build class icon đầy đủ cho row action (RowActionIconButton chỉ nhận 1 class string)
		 */
		buildRowActionIconClass(action) {
			return `mi-new-v2 mi-16 ${action.icon}`;
		},

		/**
		 * Build tooltip cho row action: hỗ trợ cả string tĩnh và function nhận dataRow
		 * RowActionIconButton nhận tooltip dạng string thuần
		 */
		buildRowActionTooltip(action) {
			const me = this;
			if (!action.tooltip) return '';
			return typeof action.tooltip == 'function'
				? action.tooltip(me.dataRow)
				: action.tooltip;
		},

		/**
		 * Xử lý click vào 1 row action, emit ra ngoài kèm dataRow để nơi dùng grid tự xử lý nghiệp vụ
		 * Không await onClick — async/await (nếu cần) là việc của widget/danh sách tự xử lý bên trong onClick,
		 * base chỉ gọi và emit ngay, không cần đợi kết quả
		 */
		onRowActionClick(action) {
			const me = this;
			if (typeof action.onClick == 'function') {
				action.onClick(me.dataRow);
			}
			me.$emit('rowAction', { key: action.key, dataRow: me.dataRow });
		},

		/**
		 * Hàm lấy dữ liệu hiển thị theo dataField. Các trường hợp muốn lấy dữ liệu từ
		 * các đối tượng nằm các cấp sâu trong đối tượng cha thì sử dụng dấu '.' để gọi tới các đối tượng bên trong
		 * @param data(object) : đối tượng muốn lấy dữ liệu
		 * @param dataField(string) : Trường muốn lấy dữ liệu (ví dụ : name hoặc childObject.name)
		 * @return(string): Dữ liệu trường đó trả về hoặc trống nếu không tìm thấy
		 *
		 * ví dụ :
		 * obj :{
		 * 	  childObj :{
		 * 			name : "Nguyễn Văn Nam"
		 * 		},
		 * 		age: 21
		 * }
		 *
		 * getDeepData(obj,"age") : 21
		 * getDeepData(obj,"childObj.name"): Nguyễn Văn Nam
		 *
		 */
		getDeepData(data, dataField) {
			//Trả về trực tiếp trường
			if (!dataField || !dataField.includes('.')) return data[dataField];

			//Nếu dataField có dấu . sẽ gọi lần lượt tới các cấp sâu hơn trong object
			dataField = dataField.split('.');
			dataField.forEach((item) => {
				if (!data) return '';
				data = data[item] || '';
			});
			return data;
		},

		handleAction(data) {
			const me = this;
			me.$emit('dataAction', data);
		},
	},
};
</script>
<style lang="scss" scoped>
@media screen and (max-width: 1366px) {
	@import '@/assets/scss/_sm-variables.scss';
	@import '@/assets/scss/components/msTrViewer.scss';
}
@media screen and (min-width: 1367px) {
	@import '@/assets/scss/_variables.scss';
	@import '@/assets/scss/components/msTrViewer.scss';
}
@for $i from 1 through 10 {
	$x: ($i - 1) * 20 + 20;
	.level-#{$i} {
		padding-left: #{$x}px !important;
	}
}
.multiple-cell-widget {
	position: sticky;
	left: 35px;
}

/deep/.attachfile-invoice,
.pdf-invoice {
	padding: 6px !important;
}
.shimmer-row {
	/deep/.attachfile-invoice {
		width: 37px !important;
		min-width: 37px !important;
	}
}
.shimmer-widget {
	width: 28px;
	min-width: 28px;
}
.shimmer-report {
	width: 48px;
	min-width: 48px;
}

/* Truncate text trong cell khi bật truncateOverflow: ẩn bớt text dài quá width cột, hover xem đầy đủ.
   max-width + overflow:hidden được set inline qua styleWidth() theo width cấu hình từng cột,
   không đụng cột khác. */
.ms-col-td-truncate {
	/* word-break:break-word của .ms-td bắt text wrap thay vì truncate → tắt trên cell truncate */
	word-break: normal;
	/* flex wrapper phải fill width td + cho phép con shrink, mặc định min-width:auto chặn shrink */
	/deep/ .flex {
		width: 100%;
		min-width: 0;
		overflow: hidden;
	}
}
.ms-col-text-truncate {
	/* min-width:0 cho phép span shrink dưới min-content trong flex container,
	   không thì min-width:auto mặc định chặn shrink → text tràn sang cột khác.
	   KHÔNG dùng flex:1 — flex:1 fill 100% width làm mất align (center/right) của wrapper,
	   gây lệch text sang trái. Span giữ shrink-only, wrapper flex lo justify. */
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	box-sizing: border-box;
	display: inline-block;
	vertical-align: middle;
}
/* col.truncate opt-in: cột template khai báo truncate=true — root element template bị ép truncate.
   Template cell tự render div/span tùy ý, không qua .ms-col-text-truncate như text thuần,
   nên cần /deep/ ép truncate lên root element đầu tiên. min-width:0 cho phép shrink trong td. */
.ms-col-td-truncate-template {
	/deep/ > * {
		min-width: 0;
		max-width: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		box-sizing: border-box;
		display: block;
	}
}
</style>